import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Button from "veneer/Button";
import DateRangeInput from "veneer/DateRangePicker";
import Stack from "veneer/Stack";

import { useAppSelector } from "cortex/ui/src/hooks";
import { Network } from "cortex/ui/src/network/model";
import { networksSelector } from "cortex/ui/src/redux-store/networks";
import { makeGetSelectOptions } from "cortex/ui/src/redux-store/selectors";
import Notification from "cortex/ui/src/ui/notification";
import { Option, Selected } from "cortex/ui/src/ui/table/types";
import { Panel } from "cortex/ui/src/view/layout";

const getNetworkSelectOptions = makeGetSelectOptions();

export type ReportFilterProps = {
  end?: string; // YYYY-MM-DD
  network?: Network;
  onRun?: (networkId: string | number, start: string, end: string) => void;
  start?: string; // YYYY-MM-DD
  submitted: boolean;
};

export default function ReportFilter({
  end,
  network,
  start,
  submitted,
}: ReportFilterProps) {
  const history = useHistory();
  const [startDate, setStartDate] = useState<Moment>(
    moment().subtract(7, "days"),
  );
  const [endDate, setEndDate] = useState<Moment>(moment().subtract(1, "days"));
  const [selected, setSelected] = useState<Selected>(() =>
    network ? { value: network.id, label: network.name } : undefined,
  );
  const [networkError, setNetworkError] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const networks = useAppSelector(networksSelector);
  const networkOptions: ReadonlyArray<Option> = useMemo(
    () => getNetworkSelectOptions(networks),
    [networks],
  );

  useEffect(() => {
    if (network) {
      setSelected({ value: network.id, label: network.name });
    } else {
      setSelected(undefined);
    }
  }, [network]);

  useEffect(() => {
    if (start) setStartDate(moment(start));
    if (end) setEndDate(moment(end));
  }, [start, end]);

  const handleNetworkSelect = useCallback((opt: Option | undefined) => {
    setNetworkError("");
    setSelected(opt);
  }, []);

  const handleRun = useCallback(() => {
    try {
      const selectedValue = selected?.value ?? null;
      if (!selectedValue) {
        setNetworkError("Please select a network.");
        return;
      }
      if (!startDate || !endDate) return;
      if (!startDate.isValid() || !endDate.isValid()) return;
      if (startDate.isAfter(endDate)) return;

      history.push(
        `/diagnostics/uptime-report/${selectedValue}/${startDate.format("YYYY-MM-DD")}/${endDate.format("YYYY-MM-DD")}`,
      );
    } catch (e) {
      setErrorMessage("Failed to run report.");
    }
  }, [endDate, history, selected, startDate]);

  return (
    <Panel borderRadius={2} title="Filter">
      <Stack spacing={2}>
        {errorMessage && <Notification error={errorMessage} />}
        <Stack alignItems="start" direction="row" spacing={2}>
          <AutocompleteSelectInput
            disableClearable
            inputProps={{
              error: !!networkError,
              helperText: networkError,
              label: "Select Network",
              required: true,
              sx: { width: "100%" },
            }}
            onChange={(_e: unknown, opt: Option | undefined) =>
              handleNetworkSelect(opt)
            }
            options={networkOptions}
            size="small"
            value={selected}
          />
          <DateRangeInput
            endDate={endDate}
            endMaxDate={moment().subtract(1, "days")}
            endMinDate={startDate}
            locale="en-US"
            onEndDateChange={(date: Moment | null) => {
              if (date) {
                setEndDate(date);
              }
            }}
            onStartDateChange={(date: Moment | null) => {
              if (date) {
                setStartDate(date);
              }
            }}
            startDate={startDate}
            startMaxDate={endDate}
          />
          <Button
            disabled={submitted}
            icon="refresh"
            loading={submitted}
            onClick={handleRun}
          >
            Run
          </Button>
        </Stack>
      </Stack>
    </Panel>
  );
}
